import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardBody, Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { format, startOfMonth, endOfMonth, subDays } from 'date-fns';
import Select from 'react-select';
import { FaDownload, FaSearch, FaShoppingBasket, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { clientActions } from '../../sagas/clientSlice';
import { getArticleSalesReport } from '../../services/saleService';
import { averageUnitPrice, buildArticleSalesPdf, formatQuantity, formatSalesAmount, quantitiesByUnit, reportDate } from '../../helpers/articleSalesReport';
import Pagination from '../Common/Pagination';

const dateValue = date => format(date, 'yyyy-MM-dd');
const selectedMonthDates = month => {
    const [year, monthNumber] = month.split('-').map(Number);
    const date = new Date(year, monthNumber - 1, 1);
    return { dateDebut: dateValue(startOfMonth(date)), dateFin: dateValue(endOfMonth(date)) };
};
const monthFilters = () => ({ dateDebut: dateValue(startOfMonth(new Date())), dateFin: dateValue(endOfMonth(new Date())), clientId: null, query: '' });
const PAGE_SIZE = 25;

const VentesStatistiques = () => {
    const dispatch = useDispatch();
    const company = useSelector(state => state.auth.company);
    const clients = useSelector(state => state.clients.clients) || [];
    const [draft, setDraft] = useState(monthFilters);
    const draftMonth = draft.dateDebut.slice(0, 7);
    const selectedMonth = /^\d{4}-\d{2}$/.test(draftMonth)
        && draft.dateDebut === `${draftMonth}-01` && draft.dateFin === selectedMonthDates(draftMonth).dateFin ? draftMonth : '';
    const [filters, setFilters] = useState(monthFilters);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState('article-asc');
    const [refresh, setRefresh] = useState(0);
    const [exportError, setExportError] = useState('');
    const options = [{ value: null, label: 'Tous les clients' }, ...clients.map(client => ({ value: client.id, label: client.entreprise || [client.nom, client.prenom].filter(Boolean).join(' ') || client.code }))];

    useEffect(() => { dispatch(clientActions.search({ query: '', page: 0, size: 4000 })); }, [dispatch]);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setError(''); setExportError('');
        getArticleSalesReport(filters, controller.signal).then(({ data }) => {
            if (controller.signal.aborted) return;
            setReport(data); setPage(0); setLoading(false);
        }).catch(() => {
            if (!controller.signal.aborted) { setError('Impossible de charger les ventes. Veuillez réessayer.'); setLoading(false); }
        });
        return () => controller.abort();
    }, [filters, refresh]);

    const apply = event => {
        event.preventDefault();
        if (!draft.dateDebut || !draft.dateFin || draft.dateDebut > draft.dateFin) {
            setFormError('Choisissez une date de début avant la date de fin.'); return;
        }
        setFormError(''); setFilters({ ...draft, query: draft.query.trim() }); setRefresh(value => value + 1);
    };
    const period = kind => {
        const today = new Date();
        const dates = kind === 'today' ? { dateDebut: dateValue(today), dateFin: dateValue(today) }
            : kind === 'week' ? { dateDebut: dateValue(subDays(today, 6)), dateFin: dateValue(today) }
                : { dateDebut: dateValue(startOfMonth(today)), dateFin: dateValue(endOfMonth(today)) };
        const next = { ...draft, ...dates, query: draft.query.trim() };
        setDraft(next); setFilters(next); setFormError('');
    };
    const clientLabel = options.find(option => option.value === filters.clientId)?.label || 'Client sélectionné';
    const articles = report?.articles || [];
    const sortedArticles = useMemo(() => {
        const rows = [...(report?.articles || [])];
        const byName = (a, b) => (a.designation || '').localeCompare(b.designation || '', 'fr', { numeric: true })
            || Number(a.articleId) - Number(b.articleId);
        return rows.sort((a, b) => {
            const [field, direction] = sort.split('-');
            const difference = field === 'article' ? byName(a, b)
                : field === 'price' ? (averageUnitPrice(a) ?? 0) - (averageUnitPrice(b) ?? 0)
                    : Number(a[field] || 0) - Number(b[field] || 0);
            return (direction === 'desc' ? -difference : difference) || byName(a, b);
        });
    }, [report, sort]);
    const totalPages = Math.ceil(articles.length / PAGE_SIZE);
    const visible = sortedArticles.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const sortColumn = field => {
        const direction = sort.startsWith(`${field}-`) ? (sort.endsWith('-asc') ? 'desc' : 'asc')
            : field === 'article' ? 'asc' : 'desc';
        setSort(`${field}-${direction}`);
        setPage(0);
    };
    const exportPdf = () => {
        setExportError('');
        try {
            buildArticleSalesPdf({ ...report, articles: sortedArticles }, { company, clientLabel, query: filters.query }).save(`ventes-par-article-${report.dateDebut}-${report.dateFin}.pdf`);
        } catch { setExportError('Impossible de créer le PDF. Veuillez réessayer.'); }
    };

    return <Container fluid className="page-content">
        <Card className="shadow-sm mb-2"><CardBody className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0"><FaShoppingBasket className="me-2" />Ventes par article</h5>
                <Button size="sm" color="primary" onClick={exportPdf} disabled={loading || !!error || !articles.length}><FaDownload className="me-2" />Télécharger le PDF</Button>
            </div>
            <div className="d-flex flex-wrap gap-1 mb-2">
                <Button size="sm" outline color="primary" onClick={() => period('today')}>Aujourd’hui</Button>
                <Button size="sm" outline color="primary" onClick={() => period('week')}>7 derniers jours</Button>
                <Button size="sm" outline color="primary" onClick={() => period('month')}>Ce mois</Button>
            </div>
            <form onSubmit={apply}>
                <Row className="g-2 align-items-end">
                    <Col xs="12" md="6" xl="2"><Label className="small mb-1" for="sales-month">Mois</Label><Input bsSize="sm" id="sales-month" type="month" value={selectedMonth} onChange={event => {
                        if (event.target.value) setDraft({ ...draft, ...selectedMonthDates(event.target.value) });
                    }} /></Col>
                    <Col xs="6" md="3" xl="2"><Label className="small mb-1" for="sales-from">Du</Label><Input bsSize="sm" id="sales-from" type="date" required value={draft.dateDebut} onChange={event => setDraft({ ...draft, dateDebut: event.target.value })} /></Col>
                    <Col xs="6" md="3" xl="2"><Label className="small mb-1" for="sales-to">Au</Label><Input bsSize="sm" id="sales-to" type="date" required value={draft.dateFin} onChange={event => setDraft({ ...draft, dateFin: event.target.value })} /></Col>
                    <Col md="6" xl="2"><Label className="small mb-1" for="sales-client">Client</Label><Select inputId="sales-client" options={options} value={options.find(option => option.value === draft.clientId)} onChange={option => setDraft({ ...draft, clientId: option?.value ?? null })} isSearchable styles={{ control: base => ({ ...base, minHeight: 31, fontSize: 14 }), valueContainer: base => ({ ...base, padding: '0 6px' }), indicatorsContainer: base => ({ ...base, height: 29 }) }} /></Col>
                    <Col md="8" xl="2"><Label className="small mb-1" for="sales-article">Chercher un article</Label><Input bsSize="sm" id="sales-article" placeholder="Nom, code ou code-barres" value={draft.query} onChange={event => setDraft({ ...draft, query: event.target.value })} /></Col>
                    <Col md="4" xl="2"><Button size="sm" color="primary" type="submit" className="w-100" disabled={loading}><FaSearch className="me-1" />Afficher les ventes</Button></Col>
                </Row>
            </form>
            {formError && <div role="alert" className="alert alert-warning mt-3 mb-0">{formError}</div>}
        </CardBody></Card>
        {exportError && <div role="alert" className="alert alert-danger">{exportError}</div>}
        {loading ? <div className="text-center py-5"><Spinner /><p>Chargement des ventes…</p></div>
            : error ? <div role="alert" className="alert alert-danger">{error} <Button onClick={() => setRefresh(value => value + 1)}>Réessayer</Button></div>
                : report && <>
                    <Card><CardBody className="p-2 p-md-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                            <small><strong>Du {reportDate(report.dateDebut)} au {reportDate(report.dateFin)}</strong> · {clientLabel}{filters.query && ` · Recherche : ${filters.query}`}</small>
                        </div>
                        <div className="d-flex flex-wrap gap-2 gap-md-4 py-2 px-2 bg-light rounded small mb-2">
                            <span>Articles vendus : <strong>{articles.length}</strong></span>
                            <span>Quantités : <strong>{quantitiesByUnit(articles) || '0'}</strong></span>
                            <span>Total ventes : <strong>{formatSalesAmount(report.totalAmount)}</strong></span>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: 'max(240px, calc(100vh - 330px))' }}><table className="table table-sm table-striped align-middle mb-0">
                            <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr>
                                {[
                                    ['article', 'Article / code-barres'], ['quantity', 'Quantité vendue'],
                                    ['price', 'Prix unitaire'], ['amount', 'Montant des ventes'],
                                ].map(([field, label]) => {
                                    const active = sort.startsWith(`${field}-`);
                                    const ascending = sort.endsWith('-asc');
                                    const SortIcon = active ? (ascending ? FaSortUp : FaSortDown) : FaSort;
                                    return <th key={field} scope="col" className={field === 'article' ? '' : 'text-end'} aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}>
                                        <button type="button" className="bg-transparent border-0 p-0 fw-bold text-nowrap" onClick={() => sortColumn(field)} title={`Trier par ${label.toLowerCase()}`}>
                                            {label} <SortIcon aria-hidden="true" className={active ? 'text-primary' : 'text-muted'} />
                                        </button>
                                    </th>;
                                })}
                            </tr></thead>
                            <tbody>{visible.map(article => <tr key={article.articleId}>
                                <td><strong>{article.designation}</strong>{article.barcode && <div className="text-muted small">{article.barcode}</div>}</td>
                                <td className="text-end fw-bold">{formatQuantity(article.quantity)} <span className="fw-normal">{article.unit || 'unité'}</span></td>
                                <td className="text-end">{averageUnitPrice(article) === null ? '—' : formatSalesAmount(averageUnitPrice(article))}</td><td className="text-end">{formatSalesAmount(article.amount)}</td>
                            </tr>)}
                                {!articles.length && <tr><td colSpan="4" className="text-center py-4">Aucune vente pour cette période. Essayez une autre période ou un autre article.</td></tr>}
                            </tbody>
                        </table></div>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                            {!!articles.length && <small className="text-muted">Articles {page * PAGE_SIZE + 1} à {Math.min((page + 1) * PAGE_SIZE, articles.length)} sur {articles.length}. Le PDF contient tous les articles.</small>}
                            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                        </div>
                        <small className="text-muted d-block">Ventes livrées ; retours non déduits. Prix unitaire = montant des ventes ÷ quantité vendue.</small>
                    </CardBody></Card>
                </>}
    </Container>;
};

export default VentesStatistiques;
